import React, { useEffect } from 'react';
import PaymentMethodCard from './PaymentMethodCard';
import Input from '../../../shared/forms/Input';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Modal, { ModalCloseButton } from '../../../shared/modals/Modal';
import { useMessage } from '../../../contexts/MessageContext';

import { ReactComponent as Spinner } from '../../../assets/icons/spinner2.svg';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const ChangeCardModalForm = ({ setChangeCardModalVisible, incrementPaymentVersion }) => {
  const showMessage = useMessage();
  const [name, setName] = React.useState('');
  const [paymentMethod, setPaymentMethod] = React.useState(null);
  const elements = useElements();
  const stripe = useStripe();
  const [processing, setProcessing] = React.useState(false);
  const [cardInputFocused, setCardInputFocused] = React.useState(false);
  const closeModal = () => setChangeCardModalVisible(false);
  const handleFocus = () => setCardInputFocused(true);
  const handleBlur = () => setCardInputFocused(false);

  const getCssToken = (token, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return value || fallback;
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: getCssToken('--font-normal', '1rem'),
        color: getCssToken('--input-text', '#151c2d'),
        iconColor: getCssToken('--input-text', '#151c2d'),
        '::placeholder': {
          color: getCssToken('--input-placeholder', '#67758f'),
        },
      },
      invalid: {
        color: getCssToken('--warning', '#d42c2c'),
      },
    },
  };

  useEffect(() => {
    fetch(process.env.REACT_APP_SERVER_URL + '/api/get-customer-payment-method', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('token'),
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setPaymentMethod(data.success ? data.paymentMethod : {});
      })
      .catch(() => setPaymentMethod({}));
  }, []);

  const handleUpdate = async () => {
    if (!stripe || !elements || processing) return;
    setProcessing(true);
    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod: nextPaymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name,
      },
    });

    if (error) {
      showMessage('Invalid card details.');
      setProcessing(false);
      return;
    }

    fetch(process.env.REACT_APP_SERVER_URL + '/api/update-payment-method', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('token'),
      },
      body: JSON.stringify({
        paymentMethodId: nextPaymentMethod.id,
      }),
    }).then((res) => res.json())
      .then((data) => {
        if (data.success) {
          incrementPaymentVersion();
          closeModal();
        }
        else {
          showMessage('Could not update your card. Please try again.');
        }
        setProcessing(false);
      })
      .catch(() => {
        showMessage('Could not update your card. Please try again.');
        setProcessing(false);
      });
  };

  return (
    <Modal isOpen={true} onClose={closeModal} className="modal--compact modal--full-height">
      <div className="modal">
        <div className="modal__header">
          <h3 className="margin-none">Change card</h3>
          <ModalCloseButton
            onClick={closeModal}
            label="Close change card modal"
          />
        </div>

        <div className="modal__body">
          <form
            id="change-card-form"
            onSubmit={(event) => {
              event.preventDefault();
              handleUpdate();
            }}
          >
            <PaymentMethodCard paymentMethod={paymentMethod} inModal={true} />

            <div className="field-stack">
              <Input
                label="Cardholder name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>

            <div className="field-stack">
              <div className={`stripe-input ${cardInputFocused ? 'is-focused' : ''}`}>
                <CardElement
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  options={cardElementOptions}
                />
              </div>
            </div>
          </form>
        </div>

        <div className="modal__footer">
          <button
            className="btn secondary"
            type="button"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="change-card-form"
            disabled={processing || !stripe || !elements}
          >
            {processing && (
              <Spinner className="modal-submit-spinner" />
            )}
            {!processing && 'Update'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ChangeCardModal = ({ setChangeCardModalVisible, incrementPaymentVersion }) => {
  return (
    <Elements stripe={stripePromise}>
      <ChangeCardModalForm
        setChangeCardModalVisible={setChangeCardModalVisible}
        incrementPaymentVersion={incrementPaymentVersion}
      />
    </Elements>
  );
};

export default ChangeCardModal;
